// React
import { Fragment, useMemo } from "react"

// Router
import { Routes } from "@/constants/routes"

// UI
import { PageTemplate } from "@/layouts/PageTemplate"
import { CardWrapper, CardBody } from "@/components/Card"
import { Heading, P } from "@/components/Typography"
import { Text } from "@visx/text"
import { TickRendererProps } from "@visx/axis"
import { MultiGraph } from "@/components/graphs/types/bar-line/MultiGraph"
import { GraphLabel, GraphLabels } from "@/components/graphs/GraphLabels"
import {
	Table,
	TableBody,
	TableDataCell,
	TableHead,
	TableRowCell,
	TableHeading,
} from "@/components/table-controls/TableItems"
import {
	getCoreRowModel,
	useReactTable,
	createColumnHelper,
	flexRender,
} from "@/lib/table"

// Components
import { AllProjectsDefaultRate } from "../_components/AllProjectsDefaultRate"
import { Tooltip } from "@/components/Tooltip"
import { InvestmentOpportunityBanner } from "@/components/banners/InvestmentOpportunityBanner"

// Auth
import { AuthStateStatusEnum, useAuth } from "@/context/useAuth"

// Icons
import { FiInfo } from "@/lib/icons"

// Translate
import { useLang } from "@/context/useLang"
import { useTrans, Trans } from "@/lib/i18n"

// Query
import { useDefaultRateDisclosureQuery } from "@/api/graphql"

// Types
import { DefaultRatePerYearType } from "@/api/graphql/types"

const columnHelper = createColumnHelper<DefaultRatePerYearType>()

export function StatisticsDefaultRateInfo() {
	// Translations
	const t = useTrans("dashboard")
	const { formatNumber } = useLang()

	// Auth
	const auth = useAuth()

	// Query
	const { data: defaultRateQueryData } = useDefaultRateDisclosureQuery()

	const defaultRateData =
		defaultRateQueryData?.platform_statistics?.default_rates ?? []

	const defaultRateYears = useMemo(() => {
		if (defaultRateData.length === 0) return { start: "", end: "" }
		return {
			start: defaultRateData[0]?.year?.toString() ?? "",
			end:
				defaultRateData[defaultRateData.length - 1]?.year?.toString() ??
				"",
		}
	}, [defaultRateData])

	// Calculate average of all default rates
	const averageRate = useMemo(() => {
		if (defaultRateData.length === 0) return "0"
		const sum = defaultRateData.reduce(
			(acc, data) => acc + (data?.default_rate ?? 0),
			0,
		)
		const average = sum / defaultRateData.length
		return average.toFixed(2)
	}, [defaultRateData])

	const barDatas = useMemo(
		() => [
			{
				id: "default_rate",
				variant: "primary" as const,
				data: defaultRateData.map((entry) => ({
					x: entry?.year?.toString() ?? "",
					y: entry?.default_rate ?? 0,
				})),
			},
		],
		[defaultRateData],
	)

	const prepareTooltipValues = useMemo(
		() => (year: string) => {
			const yearData = defaultRateData.find(
				(d) => d?.year?.toString() === year,
			)
			if (!yearData) return []

			return [
				{
					id: "denominator",
					value: formatNumber(yearData.denominator ?? 0),
					variant: "default" as const,
					colour: "#6B7280",
				},
				{
					id: "numerator",
					value: formatNumber(yearData.numerator ?? 0),
					variant: "default" as const,
					colour: "#EF4444",
				},
				{
					id: "default_rate",
					value: formatNumber(yearData.default_rate ?? 0),
					variant: "primary" as const,
					colour: MultiGraph.barVariantColourMap.primary,
				},
			]
		},
		[defaultRateData, formatNumber],
	)

	const prepareTooltipLabel = useMemo(() => (year: string) => year, [])

	const columns = useMemo(
		() => [
			columnHelper.accessor("year", {
				header: () => (
					<TableHeading variant="static">
						{t("dashboard:statistics.default_rate.table.year")}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell>{info.getValue()}</TableDataCell>
				),
			}),
			columnHelper.accessor("numerator", {
				header: () => (
					<TableHeading
						variant="static"
						tooltip={t(
							"dashboard:statistics.default_rate.table.numerator_tooltip",
						)}
					>
						{t("dashboard:statistics.default_rate.table.numerator")}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell>{info.getValue()}</TableDataCell>
				),
			}),
			columnHelper.accessor("denominator", {
				header: () => (
					<TableHeading
						variant="static"
						tooltip={t(
							"dashboard:statistics.default_rate.table.denominator_tooltip",
						)}
					>
						{t(
							"dashboard:statistics.default_rate.table.denominator",
						)}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell>{info.getValue()}</TableDataCell>
				),
			}),
			columnHelper.accessor("default_rate", {
				header: () => (
					<TableHeading variant="static">
						{t(
							"dashboard:statistics.default_rate.table.default_rate",
						)}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell>{info.getValue()}</TableDataCell>
				),
			}),
		],
		[t],
	)

	const table = useReactTable({
		columns,
		data: defaultRateData.filter(Boolean) as DefaultRatePerYearType[],
		getCoreRowModel: getCoreRowModel(),
	})

	return (
		<>
			<title>
				{t("common:web.title", {
					page: t(
						"dashboard:statistics.default_rate.title",
						defaultRateYears,
					),
				})}
			</title>

			<PageTemplate backHref={Routes.StatisticsPlatform}>
				{auth.status === AuthStateStatusEnum.LOGGED_IN ? (
					<InvestmentOpportunityBanner className="mb-4" />
				) : null}
				<AllProjectsDefaultRate />
				<CardWrapper className="mb-4">
					<CardBody>
						<Heading as="h2" className="mb-8 text-center">
							{t(
								"dashboard:statistics.default_rate.title",
								defaultRateYears,
							)}
						</Heading>
						<p className="mb-8 text-center text-base font-extrabold uppercase">
							{t(
								"dashboard:statistics.default_rate.subtitle",
								defaultRateYears,
							)}
						</p>

						<div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
							{/* Chart Section */}
							<div className="lg:col-span-2">
								<div className="h-80 w-full">
									<MultiGraph
										barDatas={barDatas}
										prepareTooltipValues={
											prepareTooltipValues
										}
										prepareTooltipLabel={
											prepareTooltipLabel
										}
										xTickComponent={
											DefaultRateYTickComponent
										}
									/>
								</div>
								<GraphLabels className="p-3 px-4 sm:p-5 sm:px-6">
									<GraphLabel
										type="bar"
										variant="primary"
										title={t(
											"common:multi_graph.tooltip.default_rate",
										)}
									/>
								</GraphLabels>
							</div>

							{/* Explanation Section */}
							<div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
								<Tooltip
									content={
										<div className="max-w-xs p-2">
											<p className="mb-1 font-semibold">
												{t(
													"dashboard:statistics.default_rate.calculation.title_tooltip",
												)}
											</p>
											<p className="text-sm">
												{t(
													"dashboard:statistics.default_rate.calculation.title_tooltip_body",
												)}
											</p>
										</div>
									}
								>
									<span className="mb-3 flex items-center">
										<Heading as="h3" styleAs="h6">
											{t(
												"dashboard:statistics.default_rate.calculation.title",
											)}
										</Heading>
										<FiInfo className="ml-2" />
									</span>
								</Tooltip>
								<P className="text-sm leading-relaxed text-gray-700">
									{t(
										"dashboard:statistics.default_rate.calculation.explanation",
									)}
								</P>
								<ul className="mt-3 list-disc space-y-1 pl-4 text-sm leading-relaxed text-gray-700">
									<li>
										{t(
											"dashboard:statistics.default_rate.calculation.point1",
										)}
									</li>
									<li>
										{t(
											"dashboard:statistics.default_rate.calculation.point2",
										)}
									</li>
								</ul>
								<P className="mt-3 text-sm leading-relaxed text-gray-700">
									{t(
										"dashboard:statistics.default_rate.calculation.summary",
									)}
								</P>
							</div>
						</div>
					</CardBody>
				</CardWrapper>

				<CardWrapper className="mb-4">
					<CardBody className="text-center">
						<Trans
							ns="dashboard"
							i18nKey="dashboard:statistics.default.average_rate_text"
							values={{
								rate: averageRate,
								...defaultRateYears,
							}}
							components={{
								strong: <strong />,
							}}
						/>
					</CardBody>
				</CardWrapper>

				<CardWrapper>
					<CardBody>
						<Table>
							<TableHead>
								{table.getHeaderGroups().map((headerGroup) => (
									<TableRowCell
										key={headerGroup.id}
										withHover={false}
									>
										{headerGroup.headers.map((header) => (
											<Fragment key={header.id}>
												{flexRender(
													header.column.columnDef
														.header,
													header.getContext(),
												)}
											</Fragment>
										))}
									</TableRowCell>
								))}
							</TableHead>
							<TableBody>
								{table.getRowModel().rows.map((row, index) => (
									<TableRowCell
										key={row.id}
										isOdd={index % 2 === 0}
									>
										{row.getVisibleCells().map((cell) => (
											<Fragment key={cell.id}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</Fragment>
										))}
									</TableRowCell>
								))}
							</TableBody>
						</Table>
					</CardBody>
				</CardWrapper>

				<P className="mt-4 text-center text-sm text-gray-600">
					{t("dashboard:statistics.default_rate.disclaimer")}
				</P>
			</PageTemplate>
		</>
	)
}

function DefaultRateYTickComponent({
	formattedValue,
	...props
}: TickRendererProps) {
	return <Text {...props}>{formattedValue ? `${formattedValue}%` : ""}</Text>
}
